import React, { useState, useEffect, useRef, memo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { apiService } from '../services/api';
import { CheckInQuestion } from '../types';

interface FormData {
  week_start_date: string;
  custom_answers?: Record<string, any>;
}

// Memoized TextArea component to prevent focus loss
const TextArea = memo<{
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  required?: boolean;
}>(({ label, value, onChange, placeholder, error, required }) => (
  <div className="mb-6">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={3}
      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
        error ? 'border-red-300' : 'border-gray-300'
      }`}
    />
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
));

// Memoized StarRating component
const StarRating = memo<{
  value: number;
  onChange: (value: 1 | 2 | 3 | 4 | 5) => void;
  label: string;
  error?: string;
}>(({ value, onChange, label, error }) => (
  <div className="mb-6">
    <label className="block text-sm font-medium text-gray-700 mb-2">
      {label}
    </label>
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onChange(star as 1 | 2 | 3 | 4 | 5)}
          className={`text-2xl transition-colors ${
            star <= value ? 'text-yellow-400' : 'text-gray-300'
          } hover:text-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 rounded`}
        >
          ★
        </button>
      ))}
      <span className="ml-2 text-sm text-gray-500">({value}/5)</span>
    </div>
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
));

const CheckInForm: React.FC = () => {
  console.log('CheckInForm rendered');
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id;
  console.log('Current id:', id);
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [customQuestions, setCustomQuestions] = useState<CheckInQuestion[]>([]);
  const [customAnswers, setCustomAnswers] = useState<Record<string, any>>({});

  const getWeekStartDate = (): string => {
    const today = new Date();
    const day = today.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    return monday.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState<FormData>(() => ({
    week_start_date: getWeekStartDate(),
  }));

  useEffect(() => {
    console.log('useEffect triggered, id:', id);
    loadActiveQuestions();
    if (id) {
      setIsEditMode(true);
      loadExistingCheckIn(parseInt(id));
    }
    // eslint-disable-next-line
  }, [id]);

  const loadActiveQuestions = async () => {
    try {
      const questions = await apiService.getActiveCheckInQuestions();
      setCustomQuestions(questions);
      // Initialize custom answers with empty values
      const initialAnswers: Record<string, any> = {};
      questions.forEach(q => {
        if (q.field_type === 'checkbox') {
          initialAnswers[`question_${q.id}`] = [];
        } else if (q.field_type === 'rating') {
          initialAnswers[`question_${q.id}`] = 0;
        } else {
          initialAnswers[`question_${q.id}`] = '';
        }
      });
      setCustomAnswers(initialAnswers);
    } catch (error) {
      console.error('Error loading active questions:', error);
    }
  };

  const loadExistingCheckIn = async (checkInId: number) => {
    try {
      setIsLoading(true);
      const checkIn = await apiService.getWeeklyCheckIn(checkInId);
      
      // Populate form with existing data
      setFormData({
        week_start_date: checkIn.week_start_date,
      });
      
      // Load custom answers if they exist
      if (checkIn.custom_answers) {
        setCustomAnswers(checkIn.custom_answers);
      }
    } catch (error) {
      console.error('Error loading check-in:', error);
      setErrors({ general: 'Failed to load check-in data. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleAnswerChange = (questionId: number, value: any) => {
    const answerKey = `question_${questionId}`;
    setCustomAnswers(prev => ({
      ...prev,
      [answerKey]: value
    }));
    
    // Clear field-specific errors
    if (errors[answerKey]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[answerKey];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Validate custom required questions
    customQuestions.forEach(question => {
      if (question.is_required && question.is_active) {
        const answerKey = `question_${question.id}`;
        const answer = customAnswers[answerKey];
        if (!answer || (typeof answer === 'string' && !answer.trim()) || 
            (Array.isArray(answer) && answer.length === 0) ||
            (question.field_type === 'rating' && answer === 0)) {
          newErrors[answerKey] = 'This field is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const submitData = {
        week_start_date: formData.week_start_date,
        custom_answers: customAnswers,
      };

      if (isEditMode && id) {
        await apiService.updateWeeklyCheckIn(parseInt(id), submitData);
        navigate('/checkins', { state: { message: 'Weekly check-in updated successfully!' } });
      } else {
        await apiService.submitWeeklyCheckIn(submitData);
        navigate('/checkins', { state: { message: 'Weekly check-in submitted successfully!' } });
      }
    } catch (error: any) {
      console.error('Error submitting check-in:', error);
      const data = error.response?.data;
      if (data) {
        if (data.errors && typeof data.errors === 'object') {
          const fieldErrors: Record<string, string> = {};
          Object.entries(data.errors).forEach(([key, value]) => {
            fieldErrors[key] = Array.isArray(value) ? value[0] : String(value);
          });
          setErrors(fieldErrors);
        } else if (data.detail) {
          setErrors({ general: typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail) });
        } else {
          const fieldErrors: Record<string, string> = {};
          Object.entries(data).forEach(([key, value]) => {
            if (key !== 'success' && value != null) {
              fieldErrors[key] = Array.isArray(value) ? value[0] : String(value);
            }
          });
          setErrors(Object.keys(fieldErrors).length > 0 ? fieldErrors : { general: 'Failed to submit check-in. Please try again.' });
        }
      } else {
        setErrors({ general: 'Failed to submit check-in. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">
          {isEditMode ? 'Edit Weekly Check-in' : 'New Weekly Check-in'}
        </h1>
        <p className="mt-2 text-gray-600">
          {isEditMode 
            ? 'Update your weekly progress update'
            : 'Complete your weekly progress update'
          }
        </p>
        <p className="mt-1 text-sm text-gray-500">
          Date: {formData.week_start_date}
        </p>
      </div>

      {(errors.general || errors.week_start_date) && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{errors.general || errors.week_start_date}</p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Dynamic Questions Section */}
        {customQuestions.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-12 text-center">
            <p className="text-gray-500 text-lg mb-4">No check-in questions have been set up yet.</p>
            <p className="text-gray-400 text-sm">Please contact your trainer to set up check-in questions.</p>
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Check-In Questions</h2>
            
            {customQuestions.map((question) => {
              const answerKey = `question_${question.id}`;
              const answer = customAnswers[answerKey] || '';
              const error = errors[answerKey];
              
              return (
                <div key={question.id} className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {question.question_text}
                    {question.is_required && <span className="text-red-500">*</span>}
                  </label>
                  
                  {question.help_text && (
                    <p className="text-sm text-gray-500 mb-2">{question.help_text}</p>
                  )}
                  
                  {question.field_type === 'text' && (
                    <input
                      type="text"
                      value={typeof answer === 'string' ? answer : ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      placeholder={question.help_text || ''}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        error ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  )}
                  
                  {question.field_type === 'textarea' && (
                    <textarea
                      value={typeof answer === 'string' ? answer : ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      placeholder={question.help_text || ''}
                      rows={3}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        error ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  )}
                  
                  {question.field_type === 'number' && (
                    <input
                      type="number"
                      step={question.question_text.toLowerCase().includes('weight') ? '0.1' : '1'}
                      value={typeof answer === 'number' ? answer : (answer || '')}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value ? parseFloat(e.target.value) : '')}
                      placeholder={question.help_text || ''}
                      min={question.question_text.toLowerCase().includes('menstrual') ? 1 : undefined}
                      max={question.question_text.toLowerCase().includes('menstrual') ? 35 : undefined}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        error ? 'border-red-300' : 'border-gray-300'
                      }`}
                    />
                  )}
                  
                  {question.field_type === 'rating' && (
                    <div className="flex items-center space-x-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => handleAnswerChange(question.id, star)}
                          className={`text-2xl transition-colors ${
                            star <= (answer as number || 0) ? 'text-yellow-400' : 'text-gray-300'
                          } hover:text-yellow-400 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 rounded`}
                        >
                          ★
                        </button>
                      ))}
                      <span className="ml-2 text-sm text-gray-500">
                        ({(answer as number || 0)}/5)
                      </span>
                    </div>
                  )}
                  
                  {question.field_type === 'select' && question.choices && (
                    <select
                      value={typeof answer === 'string' ? answer : ''}
                      onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                      className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                        error ? 'border-red-300' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Select an option...</option>
                      {question.choices.map((choice, idx) => (
                        <option key={idx} value={choice}>
                          {choice}
                        </option>
                      ))}
                    </select>
                  )}
                  
                  {question.field_type === 'checkbox' && question.choices && (
                    <div className="space-y-2">
                      {question.choices.map((choice, idx) => {
                        const checkedValues = Array.isArray(answer) ? answer : [];
                        const isChecked = checkedValues.includes(choice);
                        
                        return (
                          <label key={idx} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={(e) => {
                                const currentValues = Array.isArray(answer) ? answer : [];
                                if (e.target.checked) {
                                  handleAnswerChange(question.id, [...currentValues, choice]);
                                } else {
                                  handleAnswerChange(question.id, currentValues.filter(v => v !== choice));
                                }
                              }}
                              className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                            />
                            <span className="ml-2 text-sm text-gray-700">{choice}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                  
                  {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
                </div>
              );
            })}
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/checkins')}
            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? 'Submitting...' : (isEditMode ? 'Update Check-in' : 'Submit Check-in')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default React.memo(CheckInForm); 